import { Injectable, OnInit } from "@angular/core";
import { ThemeHelpers } from "./utilities/ThemeHelpers";
import {
    FaceTecSDKInstance,
    FaceTecSessionResult,
} from "src/assets/core-sdk/FaceTecSDK.js/FaceTecPublicApi";
import { Config as FacetecConfig } from "src/assets/Config";
import { SampleAppUtilities } from "./utilities/SampleAppUtilities";
import { SessionRequestProcessor } from "./SessionRequestProcessor";
import { FaceTecSDK } from "./utilities/FaceTecSDKGlobal";
import { Router } from "@angular/router";
import { DataService } from "./data.service";
import { Config, Data, Error, ErrorType, SkipErrorPageErrorTypes } from "../modules/facetec/dto";
import { RefService } from "./ref.service";
import { HttpFacetecService } from "./http-facetec.service";
import { catchError } from "rxjs/operators";
import { NgxSpinnerService } from "ngx-spinner";
import { ConfigService } from "./config.service";

@Injectable()
export class FaceTecSDKServiceV10 implements OnInit {
    private themeHelpers: ThemeHelpers = new ThemeHelpers();
    private faceTecSDKInstance!: FaceTecSDKInstance;

    private parsedResponse: any = {};

    // For Official ID Photo Sessions, the FaceTec Sample App demonstrates getting the generated Official ID Photo from the Server's response,
    // storing it here to display the result on a sample screen once the FaceTec SDK exits.
    public static latestOfficialIDPhoto: string = "";

    // IMPORTANT NOTE:  In Your Production Application, DO NOT set or handle externalDatabaseRefID in your client-side code.
    //
    // The externalDatabaseRefID is used in the following calls for the following reasons:
    // - 3D Enrollment - Your internal identifier for the 3D Enrollment.
    // - 3D:3D Re-Verification - Your internal identifier for the 3D Enrollment that will be used to perform 3D:3D Matching against for the 3D FaceScan that will be created.
    // - Photo ID Match - Your internal identifier for the 3D Enrollment that will be used to to perform 3D:2D Matching of the ID Images to the 3D Enrollment.
    //
    // The FaceTec Sample App demonstrates generating the externalDatabaseRefID on the client-side *FOR DEMONSTRATION PURPOSES ONLY*.
    // In Production, you need to generate and manage the externalDatabaseRefIDs in your server-side code.
    // * If you expose externalDatabaseRefIDs in your front-end code, you will allow for attacks where externalDatabaseRefIDs can be
    // exposed by to attackers by hooking into device code or inspecting network transactions.
    public static demonstrationExternalDatabaseRefID: string = "";

    constructor(
        private router: Router,
        private dataService: DataService,
        private refService: RefService,
        private httpService: HttpFacetecService,
        private spinner: NgxSpinnerService,
        private configService: ConfigService,
    ) {
        FacetecConfig.requestNumber = 0;
    }

    ngOnInit(): void {}

    onPhotoIDMatchPressed = () => {
        SampleAppUtilities.fadeOutMainUIAndPrepareForSession();

        const ref = this.refService.getRef() as string;
        const transactionId = (this.configService.getConfig() as Config).transactionId;
        const lei = FacetecConfig.demonstrationExternalDatabaseRefID;

        console.log("Starting Photo ID Match with values: ", {
            ref,
            lei,
            transactionId,
        });
        
        this.httpService.sendExternalDBRef(ref, lei, transactionId).subscribe(
            (_ret) => {},
            (_err) => {},
        );

        this.faceTecSDKInstance.start3DLivenessThen3D2DPhotoIDMatch(
            new SessionRequestProcessor(this),
        );
    };

    setFaceTecSDKInstance = (instance: FaceTecSDKInstance) => {
        this.faceTecSDKInstance = instance;
    };

    getFaceTecSDKInstance = (): FaceTecSDKInstance => {
        return this.faceTecSDKInstance;
    };

    storeParsedResponse = (parsedResponse: any) => {
        this.parsedResponse = parsedResponse;
    };

    handleFaceTecExit = (facetecSessionResult: FaceTecSessionResult): void => {
        console.log(
            "FaceTec SDK exited with status: ",
            facetecSessionResult.status,
            " and parsed response: ",
            this.parsedResponse,
        );

        const idScanResultsSoFar = this.parsedResponse.idScanResultsSoFar;
        if (idScanResultsSoFar) {
            if (idScanResultsSoFar.unexpectedMediaEncounteredAtLeastOnce) {
                console.log("Invalid ID Type (Unexpected Media)");
                this.submitErrorAudit(ErrorType.IdTypeUnexpectedMedia);
                return;
            }

            if (idScanResultsSoFar.didMatchIDScanToOCRTemplate === false) {
                console.log("OCR Template Match Failed");
                this.submitErrorAudit(ErrorType.OcrTemplateMatchFailed);
                return;
            }
        }

        if (facetecSessionResult.status === FaceTecSDK.FaceTecSessionStatus.SessionCompleted) {
            const data: Data = {
                ...this.parsedResponse.axonExtractedData,
                ref: this.refService.getRef() as string,
            };

            this.dataService.setData(data);

            const axonFlasgs = this.parsedResponse.axonFlags;
            this.dataService.setAxonFlags(axonFlasgs);

            this.router.navigate(["/review"]);
        }
    };

    private submitErrorAudit(errorType: ErrorType): void {
        this.spinner.show();
        const errors: Error[] = this.configService.getReceivedErrors() || [];
        const ref = this.refService.getRef() as string;
        var error: Error = {
            errorType: errorType,
            data: { ...this.dataService.getData() } as Data,
        };

        console.log("Setting Last Error", {
            error: { ...error, data: { ...error.data, imageList: [] } },
        });

        this.httpService
            .audit(ref, error)
            .pipe(
                catchError((error) => {
                    this.spinner.hide();
                    console.log("Error submitting audit data", error);
                    //Save the error in the errors array so it can be submitted later
                    errors.push(error);
                    this.configService.setRetrievedErrors(errors);
                    this.configService.setRetryCounter(0);
                    this.router.navigate(["/error"]);
                    throw error;
                }),
            )
            .subscribe((result) => {
                this.spinner.hide();
                this.configService.setRetryCounter(0);
                console.log("Submitted audit data successfully", {
                    result: {
                        ...result,
                        photoIDFaceCrop: "",
                        scanResultBlob: "",
                        photoIDFrontCrop: "",
                        photoIDBackCrop: "",
                    },
                });
                console.log("SkipErrorPageErrorTypes", { SkipErrorPageErrorTypes });
                console.log("errorType", { errorType });
                console.log("!SkipErrorPageErrorTypes.includes(errorType)", {
                    shouldNavigate: !SkipErrorPageErrorTypes.includes(errorType),
                });
                if (!SkipErrorPageErrorTypes.includes(errorType)) {
                    this.router.navigate(["/error"]);
                }
            });
    }
}
